import React, { useEffect, useRef, useState } from 'react';
import './TripCategoryReminderModal.css';

export type TripCategory = 'International' | 'Domestic' | 'Inbound';
export type TransportMode = 'air' | 'land' | 'sea';

interface TripCategoryReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripCategory: TripCategory;
  transportMode?: TransportMode; // Only relevant for Domestic category
}

const TripCategoryReminderModal: React.FC<TripCategoryReminderModalProps> = ({
  isOpen,
  onClose,
  tripCategory,
  transportMode: initialTransportMode
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  
  // State for transport mode selection within the modal (for Domestic category)
  const [selectedTransportMode, setSelectedTransportMode] = useState<TransportMode>(initialTransportMode || 'air');

  // Initialize transport mode when modal opens
  useEffect(() => {
    if (isOpen && tripCategory === 'Domestic') {
      setSelectedTransportMode(initialTransportMode || 'air');
    }
  }, [isOpen, tripCategory, initialTransportMode]);

  useEffect(() => {
    if (isOpen) {
      // Focus the close button when modal opens
      closeButtonRef.current?.focus();
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Handle transport mode change
  const handleTransportModeChange = (mode: TransportMode) => {
    setSelectedTransportMode(mode);
  };

  // Content mapping function based on trip category and transport mode
  const getModalContent = () => {
    // For Domestic category with land/sea transport mode, show ShortSecure content
    if (tripCategory === 'Domestic' && (selectedTransportMode === 'land' || selectedTransportMode === 'sea')) {
      return {
        subtitle: 'Please read to avoid invalidating your travelsafe insurance.',
        items: [
          'Open to persons ages six (6) weeks old to seventy-five (75) years old.',
          'Travel using air transportation is not covered.',
          'Coverage shall commence at the time of departure of the Insured Person from the point of origin which may be the accommodation such as residence or employment, hotels, universities or schools, or the terminal wherein the transport vehicle to be used for the land or water trip will set off and ends on the early date of arrival at the point of origin, whichever occurs first.',
          'Coverage is allowed for travel using rented, public or company vehicles as well as private cars even when the Insured Person is driving.',
          'Accidents that occur outside the established route or town/cities in between the point of origin up to the place of destination are not covered.',
          'MAXIMUM PERIOD OF COVERAGE: Up to five (5) days per trip for ShortSecure Group and 15 days per trip for ShortSecure Individual.'
        ]
      };
    }
    
    // Default content for all other cases (International, Inbound, Domestic with air transport)
    return {
      subtitle: 'Please read to avoid invalidating your Travelsafe insurance.',
      items: [
        'The maximum period of insurance or coverage period for our Single Trip Plans is 180 days.',
        'Our Multi-Trip Plans cover an unlimited number of trips for each Policy year, provided that the maximum number of days for each business or pleasure trip to be covered under this insurance shall be 90 days.',
        'Please purchase Travelsafe Insurance on or before your scheduled departure date.',
        'Please ensure that you do not purchase multiple Travelsafe Insurance Plans with overlapping dates.',
        <>
          The coverage period of your Travelsafe Insurance cannot be extended online. If you are already insured and have begun traveling, or are already at your destination and wish for additional days of coverage, or if you have already returned to your point of origin earlier than the expiry date of your insurance period, please e-mail{' '}
          <a
            href="mailto:traveldirect_team@pacificcross.com.ph"
            className="trip-category-modal-email-link"
          >
            traveldirect_team@pacificcross.com.ph
          </a>
        </>,
        <>
          If you have purchased Travelsafe Insurance online and wish to change your coverage period, or any detail of your application, please e-mail{' '}
          <a
            href="mailto:traveldirect_team@pacificcross.com.ph"
            className="trip-category-modal-email-link"
          >
            traveldirect_team@pacificcross.com.ph
          </a>
        </>
      ]
    };
  };

  const modalContent = getModalContent();

  if (!isOpen) return null;

  return (
    <div 
      className="trip-category-modal-backdrop" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="trip-category-modal-title"
      aria-describedby="trip-category-modal-description"
    >
      <div className="trip-category-modal" ref={modalRef}>
        <div className="trip-category-modal-header">
          <div className="trip-category-modal-header-content">
            <div className="trip-category-modal-title-section">
              <div className="trip-category-modal-icon">
                <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M26.5055 24.5032C26.1076 24.5028 25.7262 24.3447 25.4448 24.0636C25.1634 23.7825 25.0052 23.4013 25.0048 23.0038V13.5076C25.002 11.4403 24.2878 9.43679 22.982 7.83309C21.6762 6.2294 19.8582 5.12303 17.8329 4.69953C18.1642 4.34312 18.3841 3.89787 18.4658 3.41838C18.5475 2.93889 18.4874 2.446 18.2928 2.00016C18.0982 1.55431 17.7776 1.17489 17.3704 0.908413C16.9631 0.641939 16.4868 0.5 16 0.5C15.5132 0.5 15.0369 0.641939 14.6296 0.908413C14.2223 1.17489 13.9018 1.55431 13.7072 2.00016C13.5126 2.446 13.4524 2.93889 13.5341 3.41838C13.6158 3.89787 13.8358 4.34312 14.1671 4.69953C12.1418 5.12303 10.3238 6.2294 9.01796 7.83309C7.71216 9.43679 6.998 11.4403 6.99523 13.5076V23.0038C6.9948 23.4013 6.83654 23.7825 6.55518 24.0636C6.27382 24.3447 5.89234 24.5028 5.49444 24.5032C4.83126 24.5039 4.19544 24.7674 3.72651 25.2359C3.25757 25.7044 2.99381 26.3397 2.99312 27.0022V29.0014C2.99308 29.0671 3.00599 29.1321 3.03112 29.1927C3.05625 29.2534 3.09309 29.3085 3.13955 29.3549C3.18602 29.4013 3.24118 29.4381 3.30189 29.4633C3.3626 29.4884 3.42767 29.5013 3.49338 29.5012H12.1452C12.3621 30.3584 12.859 31.1187 13.5572 31.6619C14.2554 32.2051 15.1151 32.5 16 32.5C16.8849 32.5 17.7446 32.2051 18.4428 31.6619C19.141 31.1187 19.6379 30.3584 19.8548 29.5012H28.5066C28.5723 29.5013 28.6374 29.4884 28.6981 29.4633C28.7588 29.4381 28.814 29.4013 28.8604 29.3549C28.9069 29.3085 28.9437 29.2534 28.9689 29.1927C28.994 29.1321 29.0069 29.0671 29.0069 29.0014V27.0022C29.0062 26.3397 28.7424 25.7044 28.2735 25.2359C27.8045 24.7674 27.1687 24.5039 26.5055 24.5032ZM16 1.51238C16.2968 1.51238 16.587 1.60032 16.8338 1.76508C17.0806 1.92983 17.273 2.16401 17.3865 2.43799C17.5001 2.71197 17.5299 3.01345 17.4719 3.3043C17.414 3.59516 17.2711 3.86233 17.0612 4.07202C16.8513 4.28172 16.5839 4.42452 16.2928 4.48238C16.0017 4.54023 15.6999 4.51054 15.4257 4.39705C15.1514 4.28357 14.917 4.09138 14.7521 3.84481C14.5872 3.59823 14.4992 3.30834 14.4992 3.01179C14.4996 2.61425 14.6579 2.23313 14.9392 1.95203C15.2206 1.67093 15.6021 1.51282 16 1.51238ZM7.99576 13.5076C7.99576 11.3867 8.83906 9.35269 10.3401 7.853C11.8412 6.35331 13.8771 5.51079 16 5.51079C18.1228 5.51079 20.1588 6.35331 21.6598 7.853C23.1609 9.35269 24.0042 11.3867 24.0042 13.5076V21.5044H7.99576V13.5076ZM7.99576 23.0038V22.504H24.0042V23.0038C24.0061 23.5463 24.1863 24.0731 24.5171 24.5032H7.48286C7.81366 24.0731 7.99387 23.5463 7.99576 23.0038ZM16 31.5004C15.3808 31.4976 14.7776 31.3042 14.2725 30.9464C13.7674 30.5886 13.3849 30.084 13.1773 29.5012H18.8226C18.615 30.084 18.2326 30.5886 17.7275 30.9464C17.2224 31.3042 16.6192 31.4976 16 31.5004ZM28.0063 28.5016H3.99364V27.0022C3.99408 26.6047 4.15234 26.2236 4.4337 25.9425C4.71506 25.6614 5.09654 25.5033 5.49444 25.5028H26.5055C26.9034 25.5033 27.2849 25.6614 27.5663 25.9425C27.8476 26.2236 28.0059 26.6047 28.0063 27.0022V28.5016ZM29.5687 11.9711C29.4649 11.8885 29.3325 11.8505 29.2007 11.8654C29.0689 11.8804 28.9484 11.947 28.8657 12.0507C28.783 12.1544 28.745 12.2866 28.7599 12.4183C28.7749 12.55 28.8416 12.6704 28.9454 12.753C30.0557 13.6506 30.7765 14.9418 30.9575 16.3573C31.1384 17.7728 30.7655 19.2035 29.9166 20.3511C29.8377 20.4573 29.8042 20.5903 29.8233 20.7212C29.8424 20.852 29.9126 20.97 30.0185 21.0492C30.1245 21.1285 30.2575 21.1626 30.3886 21.1441C30.5196 21.1255 30.638 21.0559 30.7178 20.9504C31.7208 19.594 32.1611 17.9032 31.9471 16.2304C31.733 14.5575 30.881 13.0318 29.5687 11.9711ZM27.5158 18.551C27.4763 18.6034 27.4474 18.6631 27.4309 18.7267C27.4145 18.7903 27.4107 18.8565 27.4199 18.9215C27.4291 18.9865 27.451 19.0491 27.4844 19.1056C27.5178 19.1622 27.562 19.2116 27.6145 19.2511C27.6671 19.2905 27.7269 19.3193 27.7905 19.3356C27.8542 19.352 27.9204 19.3556 27.9855 19.3463C28.0506 19.3371 28.1132 19.3151 28.1697 19.2816C28.2263 19.2482 28.2757 19.2039 28.3151 19.1513C28.857 18.4202 29.0947 17.5079 28.9783 16.6056C28.8619 15.7033 28.4005 14.881 27.6908 14.311C27.5868 14.229 27.4546 14.1916 27.323 14.2069C27.1915 14.2222 27.0714 14.2891 26.9891 14.3927C26.9069 14.4964 26.8691 14.6284 26.8841 14.7599C26.8992 14.8913 26.9658 15.0114 27.0693 15.0939C27.5761 15.5011 27.9055 16.0883 27.9887 16.7327C28.072 17.3771 27.9025 18.0286 27.5158 18.551ZM3.05467 12.753C3.10606 12.7121 3.14888 12.6615 3.1807 12.6041C3.21251 12.5467 3.23269 12.4836 3.24009 12.4183C3.24749 12.3531 3.24195 12.2871 3.22381 12.224C3.20566 12.1609 3.17525 12.102 3.13433 12.0507C3.0934 11.9993 3.04275 11.9566 2.98527 11.9248C2.92779 11.893 2.86461 11.8728 2.79933 11.8654C2.73405 11.858 2.66796 11.8636 2.60482 11.8817C2.54168 11.8998 2.48273 11.9302 2.43134 11.9711C1.119 13.0318 0.26701 14.5575 0.052931 16.2303C-0.161148 17.9032 0.279205 19.594 1.28221 20.9504C1.362 21.0559 1.48037 21.1255 1.61142 21.1441C1.74247 21.1626 1.87552 21.1285 1.98146 21.0492C2.0874 20.97 2.15761 20.852 2.17671 20.7212C2.19582 20.5903 2.16228 20.4573 2.08342 20.3511C1.23446 19.2035 0.861586 17.7728 1.04253 16.3573C1.22348 14.9417 1.94427 13.6506 3.05467 12.753ZM3.68489 19.1513C3.7243 19.2039 3.7737 19.2482 3.83025 19.2816C3.88681 19.3151 3.94941 19.3371 4.01448 19.3463C4.07955 19.3556 4.1458 19.352 4.20946 19.3356C4.27312 19.3193 4.33292 19.2905 4.38546 19.2511C4.43799 19.2116 4.48222 19.1622 4.51562 19.1056C4.54902 19.0491 4.57093 18.9865 4.58009 18.9215C4.58926 18.8565 4.5855 18.7903 4.56904 18.7267C4.55258 18.6631 4.52373 18.6034 4.48414 18.551C4.09749 18.0286 3.92804 17.3771 4.01127 16.7327C4.09449 16.0883 4.42394 15.5011 4.93067 15.0939C5.03424 15.0114 5.10085 14.8913 5.11589 14.7599C5.13093 14.6284 5.09316 14.4964 5.01088 14.3927C4.9286 14.2891 4.80852 14.2222 4.67698 14.2069C4.54545 14.1916 4.4132 14.229 4.30925 14.311C3.5995 14.881 3.13809 15.7033 3.0217 16.6056C2.90531 17.5079 3.14298 18.4202 3.68489 19.1513Z" fill="black"/>
                </svg>
              </div>
              <h2 id="trip-category-modal-title" className="trip-category-modal-title">
                Reminder
              </h2>
            </div>
            <p id="trip-category-modal-description" className="trip-category-modal-subtitle">
              {modalContent.subtitle}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            className="trip-category-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>
        
        <div className="trip-category-modal-content">
          {/* Transport Mode Selection for Domestic Category */}
          {tripCategory === 'Domestic' && (
            <div className="trip-category-modal-transport-selection">
              <label className="trip-category-modal-transport-label">Select your mode of travel:</label>
              <div className="trip-category-modal-transport-options">
                <label className="trip-category-modal-transport-option">
                  <input
                    type="radio"
                    name="transportMode"
                    value="air"
                    checked={selectedTransportMode === 'air'}
                    onChange={() => handleTransportModeChange('air')}
                  />
                  <span className="trip-category-modal-transport-custom"></span>
                  Via Air
                </label>
                <label className="trip-category-modal-transport-option">
                  <input
                    type="radio"
                    name="transportMode"
                    value="land"
                    checked={selectedTransportMode === 'land'}
                    onChange={() => handleTransportModeChange('land')}
                  />
                  <span className="trip-category-modal-transport-custom"></span>
                  Via Land or Sea
                </label>
              </div>
            </div>
          )}
          
          <ul className="trip-category-modal-list">
            {modalContent.items.map((item, index) => (
              <li key={index}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TripCategoryReminderModal;